import ActionButton from 'components/ActionButton';
import { BasketRow } from 'components/Basket';
import {
	LoadingBasketRowSkeletonItem,
	LoadingSkeleton,
} from 'components/LoadingSkeleton';
import Price from 'components/Price';
import type { BasketRowResponse, FormattedPrice } from 'models/api';
import type { DiscountType } from 'models/product';
import type { CampaignId } from 'types';
import { range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

export interface AddCampaignData {
	discountType: DiscountType;
	id: CampaignId;
	rows: BasketRowResponse[];
	title: string;
}

interface Props {
	discountType: DiscountType;
	id: CampaignId;
	isLoading: boolean;
	isSellable: boolean | undefined;
	onAddToCartClick: ((campaign: AddCampaignData) => Promise<void>) | undefined;
	onAddToWishlistClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	packagePrice: FormattedPrice | undefined;
	priceLabel: string | undefined;
	rows: BasketRowResponse[] | undefined;
	title: string;
}

export default function CampaignBuyAllProducts({
	discountType,
	id,
	isLoading,
	isSellable = true,
	onAddToCartClick,
	onAddToWishlistClick,
	packagePrice,
	priceLabel,
	rows,
	title,
}: Props) {
	const { t } = useI18n();

	if (isLoading) {
		return (
			<LoadingSkeleton className="space-y-8">
				{range(2).map((i) => (
					<LoadingBasketRowSkeletonItem key={i} hasStoreStock />
				))}
			</LoadingSkeleton>
		);
	}

	if (!rows) {
		return null;
	}

	return (
		<>
			<ul className="space-y-8">
				{rows.map((row) => (
					<li key={row.variantId}>
						<BasketRow hasStoreStock row={row} subtitleVariant="brand-model" />
					</li>
				))}
			</ul>
			<div className="mt-r-lg space-y-2">
				{packagePrice && <Price price={packagePrice} label={priceLabel} />}
				{onAddToCartClick && (
					<ActionButton
						displayWidth="full"
						size="lg"
						variant="cta"
						leadingIcon="cart"
						disabled={!isSellable}
						onClick={() => onAddToCartClick({ discountType, id, title, rows })}
					>
						{t('general_add_to_cart_button_text')}
					</ActionButton>
				)}
				{onAddToWishlistClick && (
					<div className="w-full">
						<ActionButton
							displayWidth="full"
							variant="secondary"
							leadingIcon="shopping-list"
							onClick={() =>
								onAddToWishlistClick({ discountType, id, title, rows })
							}
						>
							{t('general_add_to_wishlist_button_text')}
						</ActionButton>
					</div>
				)}
			</div>
		</>
	);
}
CampaignBuyAllProducts.displayName = 'CampaignBuyAllProducts';
